import { type ReactNode, useContext } from 'react';
import { Link } from 'react-router-dom';
import { FiHome, FiLock } from 'react-icons/fi';
import { AuthContext } from '@context/AuthContext';
import { FarmContext } from '@context/FarmContext';
import { navigationItems } from '@components/navigation/config';
import { canAccessNavigationItem } from '@components/navigation/utils';
import {
  EmptyStateAction,
  EmptyStateCard,
  EmptyStateDescription,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateWrapper,
} from '@errors/ConfirmInfoError/style';
import { appRoutes } from './appRoutes';

type Props = {
  /**
   * Chave do item de navegação (`navigationItems`) cuja regra de acesso a
   * rota reusa — fonte única de verdade: a mesma função que decide o que
   * aparece no menu decide se a URL direta abre (Achado C da auditoria de
   * permissões/licenças). A exigência de fazenda selecionada continua com o
   * `ConfirInfosError`; a segurança real continua no backend.
   */
  navKey: string;
  children: ReactNode;
};

export function RouteAccessGuard({ navKey, children }: Props) {
  const { isAuthenticated, user } = useContext(AuthContext);
  const { farm, licenseMode, licenseFeatures } = useContext(FarmContext);

  const item = navigationItems.find((navItem) => navItem.key === navKey);

  const accessible =
    !item ||
    canAccessNavigationItem(
      item,
      { isAuthenticated, user, farm, licenseMode, licenseFeatures },
      { ignoreFarmRequirement: true },
    );

  if (accessible) {
    return <>{children}</>;
  }

  return (
    <EmptyStateWrapper>
      <EmptyStateCard>
        <EmptyStateIcon $variant="warning">
          <FiLock />
        </EmptyStateIcon>
        <EmptyStateTitle>Você não tem acesso a esta área</EmptyStateTitle>
        <EmptyStateDescription>
          Seu perfil ou a licença atual do sistema não permitem abrir esta
          página. Fale com um administrador se precisar deste acesso.
        </EmptyStateDescription>
        <Link style={{ textDecoration: 'none' }} to={appRoutes.dashboard}>
          <EmptyStateAction>
            <FiHome />
            Ir para o Dashboard
          </EmptyStateAction>
        </Link>
      </EmptyStateCard>
    </EmptyStateWrapper>
  );
}
